import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../supabaseClient';

interface User {
  id: string;
  username: string | null;
  pseudo: string;
  subscriber_count: number | null;
  avatar_url: string | null;
  has_youtube_channel: boolean;
  points: number;
}

interface AuthContextType {
  user: User | null;
  userId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const loadedUserIdRef = useRef<string | null>(null);

  const fetchAndUpdateUserData = useCallback(async (session: any) => {
    if (!session?.provider_token || !session?.user) {
      return null;
    }

    try {
      // Try to fetch YouTube channel data
      const response = await fetch('https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&mine=true', {
        headers: {
          'Authorization': `Bearer ${session.provider_token}`
        }
      });

      let channelInfo = null;

      if (response.ok) {
        const channelData = await response.json();
        if (channelData.items && channelData.items.length > 0) {
          const channel = channelData.items[0];
          channelInfo = {
            youtube_id: channel.id,
            username: channel.snippet.customUrl?.replace('@', '') || null,
            pseudo: channel.snippet.title,
            subscriber_count: parseInt(channel.statistics.subscriberCount) || 0,
            avatar_url: channel.snippet.thumbnails?.default?.url || channel.snippet.thumbnails?.medium?.url || null,
            has_youtube_channel: true
          };
        }
      }

      // If no YouTube channel, create a guest user with Google account info
      if (!channelInfo) {
        const email = session.user.email;
        const googleName = session.user.user_metadata?.full_name || session.user.user_metadata?.name;
        const googleAvatar = session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture;
        const guestUsername = googleName || email?.split('@')[0] || `user_${session.user.id.substring(0, 8)}`;

        channelInfo = {
          youtube_id: null,
          username: null,
          pseudo: guestUsername,
          subscriber_count: null,
          avatar_url: googleAvatar || null,
          has_youtube_channel: false
        };
      }

      const { error: updateError } = await supabase
        .from('users')
        .upsert({
          id: session.user.id,
          username: channelInfo.username,
          pseudo: channelInfo.pseudo,
          youtube_id: channelInfo.youtube_id,
          subscriber_count: channelInfo.subscriber_count,
          avatar_url: channelInfo.avatar_url,
          has_youtube_channel: channelInfo.has_youtube_channel
        });

      if (updateError) {
        console.error('Upsert error:', updateError);
        return null;
      }

      return {
        id: session.user.id,
        username: channelInfo.username,
        pseudo: channelInfo.pseudo,
        subscriber_count: channelInfo.subscriber_count,
        avatar_url: channelInfo.avatar_url,
        has_youtube_channel: channelInfo.has_youtube_channel,
        points: 0,
      };
    } catch (err) {
      console.error('fetchAndUpdateUserData error:', err);
      return null;
    }
  }, []);

  const loadUser = useCallback(async (session: any) => {
    if (!session?.user) {
      setUser(null);
      setUserId(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      loadedUserIdRef.current = null;
      return;
    }

    // Skip if already loaded for this user
    if (loadedUserIdRef.current === session.user.id) {
      return;
    }
    loadedUserIdRef.current = session.user.id;

    setUserId(session.user.id);
    setIsAuthenticated(true);

    // Check if user exists in database
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, username, pseudo, subscriber_count, avatar_url, has_youtube_channel, points')
      .eq('id', session.user.id)
      .maybeSingle();

    if (existingUser?.pseudo) {
      setUser(existingUser as User);
      setIsLoading(false);
    } else if (session.provider_token) {
      // New user - fetch YouTube data and create
      const newUser = await fetchAndUpdateUserData(session);
      if (newUser) {
        setUser(newUser);
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [fetchAndUpdateUserData]);

  useEffect(() => {
    // Listen for auth changes - INITIAL_SESSION is fired on mount
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadUser(session);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserId(null);
        setIsAuthenticated(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUser]);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: 'https://www.googleapis.com/auth/youtube.readonly',
        redirectTo: window.location.origin
      }
    });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, userId, isAuthenticated, isLoading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
